/**
 * Resource claims service — client surface for allocation follow-through (WS-B1).
 *
 * PRD: docs/9-exploration/PRD-scorecard-to-strongest.md.
 * Schema + RPCs: supabase/migrations/681_plan_resource_claims.sql.
 * Judgment (divergence tolerance, plain-language verdicts): src/lib/commitmentTerms.ts.
 *
 * All writes go through the SECURITY DEFINER RPCs (Build Contract §2.3a).
 * Claims are append-only: there is no update call here on purpose — a change
 * is a new claim passed with supersedesClaimId (L3). Reads go through the
 * typed client against the follow-through read models.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Database } from '../types/database.generated';
import type { ClaimKind } from './commitmentTerms';

export type ResourceClaimFollowThroughRow =
  Database['public']['Views']['resource_claims_followthrough']['Row'];
export type ResourceClaimEnvelopeRollupRow =
  Database['public']['Views']['resource_claims_envelope_rollup']['Row'];
export type PlanResourceClaimRow = Database['public']['Tables']['plan_resource_claims']['Row'];

export interface ClaimWriteResult {
  ok: boolean;
  claimId?: string;
  error?: string;
}

function toResult(data: unknown, error: { message: string } | null): ClaimWriteResult {
  if (error) return { ok: false, error: error.message };
  return { ok: true, claimId: typeof data === 'string' ? data : undefined };
}

export async function listFollowThroughForCycle(
  planCycleId: string
): Promise<{ rows: ResourceClaimFollowThroughRow[]; error?: string }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('resource_claims_followthrough')
    .select('*')
    .eq('plan_cycle_id', planCycleId)
    .order('created_at', { ascending: true });
  if (error) return { rows: [], error: error.message };
  return { rows: data ?? [] };
}

/**
 * Raw claim rows for a cycle — the append-only ledger itself, not the
 * follow-through comparison.
 *
 * Why this exists alongside {@link listFollowThroughForCycle}: nothing ever
 * writes `plan_records.allocated_hours`, so the follow-through view's hours
 * leg is permanently `basis_state='no_basis'` (verified against prod,
 * docs/9-exploration/DISPLACEMENT-D0-VERIFICATION-RECORD.md D0-4). Commitment
 * state (D1) is computed from what was DECLARED against the recipe-derived
 * requirement, which the raw rows carry and the view's hours comparison
 * cannot. Callers wanting declared-vs-live still use the view.
 */
export async function listClaimsForCycle(
  planCycleId: string
): Promise<{ rows: PlanResourceClaimRow[]; error?: string }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('plan_resource_claims')
    .select('*')
    .eq('plan_cycle_id', planCycleId)
    .order('created_at', { ascending: true });
  if (error) return { rows: [], error: error.message };
  return { rows: data ?? [] };
}

/** Every claim on several cycles at once, history included, oldest first. */
export async function listClaimsForCycles(
  planCycleIds: readonly string[]
): Promise<{ rows: PlanResourceClaimRow[]; error?: string }> {
  if (planCycleIds.length === 0) return { rows: [] };
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('plan_resource_claims')
    .select('*')
    .in('plan_cycle_id', planCycleIds as string[])
    .order('created_at', { ascending: true });
  if (error) return { rows: [], error: error.message };
  return { rows: data ?? [] };
}

export async function listEnvelopeRollups(): Promise<{
  rows: ResourceClaimEnvelopeRollupRow[];
  error?: string;
}> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.from('resource_claims_envelope_rollup').select('*');
  if (error) return { rows: [], error: error.message };
  return { rows: data ?? [] };
}

export interface DeclareClaimInput {
  planCycleId: string;
  itemId: string;
  claimKind: ClaimKind;
  declaredHours?: number;
  declaredAmount?: number;
  currency?: string;
  fundingEnvelopeId?: string;
  resourceType?: string;
  resourceName?: string;
  /** ISO dates (YYYY-MM-DD). */
  periodStart?: string;
  periodEnd?: string;
  basis?: string;
  /** Engine link enabling the hours comparison (plan_records join). */
  planningPriorityId?: string;
  /** L3: a change to a claim is a new claim superseding the old one. */
  supersedesClaimId?: string;
}

export async function declareClaim(input: DeclareClaimInput): Promise<ClaimWriteResult> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('declare_resource_claim', {
    p_plan_cycle_id: input.planCycleId,
    p_item_id: input.itemId,
    p_claim_kind: input.claimKind,
    p_declared_hours: input.declaredHours ?? undefined,
    p_declared_amount: input.declaredAmount ?? undefined,
    p_currency: input.currency ?? undefined,
    p_funding_envelope_id: input.fundingEnvelopeId ?? undefined,
    p_resource_type: input.resourceType ?? undefined,
    p_resource_name: input.resourceName ?? undefined,
    p_period_start: input.periodStart ?? undefined,
    p_period_end: input.periodEnd ?? undefined,
    p_basis: input.basis ?? undefined,
    p_planning_priority_id: input.planningPriorityId ?? undefined,
    p_supersedes_claim_id: input.supersedesClaimId ?? undefined,
  });
  return toResult(data, error);
}

export async function withdrawClaim(claimId: string, basis: string): Promise<ClaimWriteResult> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('withdraw_resource_claim', {
    p_claim_id: claimId,
    p_basis: basis,
  });
  return toResult(data, error);
}
